import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import i18n from '../../../../../i18n';
import { Icon } from '../../shared/Icon';
import { useDrillFocus } from './useDrillFocus';

type RegistryEntry = {
  title: string;
  body: React.ReactNode;
};

type DrillDeckContextValue = {
  metricKey: string;
  focusId: string | null;
  setFocusId: (id: string | null) => void;
  register: (sectionId: string, title: string, body: React.ReactNode) => void;
  unregister: (sectionId: string) => void;
  isFocused: (sectionId: string) => boolean;
  /** DOM id of this deck's inspector (tiles point aria-controls at it). */
  inspectorId: string;
};

const DrillDeckContext = createContext<DrillDeckContextValue | null>(null);

export function useDrillDeck(): DrillDeckContextValue {
  const ctx = useContext(DrillDeckContext);
  if (!ctx) {
    throw new Error('useDrillDeck must be used inside DrillDeck');
  }
  return ctx;
}

export function DrillDeck({
  metricKey,
  contextKey,
  focusScope,
  suspended = false,
  children,
}: {
  metricKey: string;
  /** Scope, outlet and period: a remembered inspector only reopens in the same one. */
  contextKey?: string;
  /**
   * Separate focus memory for a deck shown on top of another (the outlet
   * drawer), so neither reads, drops nor clears the other's remembered tile.
   */
  focusScope?: string;
  /** Another layer sits on top (the outlet drawer): leave Escape to it. */
  suspended?: boolean;
  children: React.ReactNode;
}) {
  const [focusId, setFocusId] = useDrillFocus(metricKey, contextKey, focusScope);
  const inspectorId = focusScope
    ? `ddrill-inspector-panel-${focusScope}`
    : 'ddrill-inspector-panel';
  const registry = useRef(new Map<string, RegistryEntry>());
  const focusIdRef = useRef(focusId);
  focusIdRef.current = focusId;
  const [, bump] = useState(0);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  /** The tile that opened the inspector; focus goes back to it on close. */
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const register = useCallback(
    (sectionId: string, title: string, body: React.ReactNode) => {
      const existed = registry.current.has(sectionId);
      registry.current.set(sectionId, { title, body });
      // Remount/reconcile inspector only when the focused tile updates, or on first register.
      if (!existed || focusIdRef.current === sectionId) {
        bump(n => n + 1);
      }
    },
    [],
  );

  const unregister = useCallback((sectionId: string) => {
    if (!registry.current.has(sectionId)) return;
    registry.current.delete(sectionId);
    bump(n => n + 1);
  }, []);

  const isFocused = useCallback(
    (sectionId: string) => focusId === sectionId,
    [focusId],
  );

  useEffect(() => {
    if (!focusId || suspended) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFocusId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusId, setFocusId, suspended]);

  // Tiles register in their own effects, which run before this one: a
  // remembered focus whose tile is not on this screen must not open later
  // on whatever tile happens to reuse the slot.
  useEffect(() => {
    const id = focusIdRef.current;
    if (id && !registry.current.has(id)) setFocusId(null);
    // Mount only: tiles that register later are opened by the user.
  }, []);

  useEffect(() => {
    if (!focusId) {
      const back = returnFocusRef.current;
      returnFocusRef.current = null;
      if (back?.isConnected) back.focus({ preventScroll: true });
      return;
    }
    const opener = document.activeElement;
    if (
      opener instanceof HTMLElement &&
      opener !== document.body &&
      !opener.closest('.ddrill-inspector')
    ) {
      returnFocusRef.current = opener;
    }
    closeBtnRef.current?.focus();
  }, [focusId]);

  const value = useMemo(
    () => ({
      metricKey,
      focusId,
      setFocusId,
      register,
      unregister,
      isFocused,
      inspectorId,
    }),
    [metricKey, focusId, setFocusId, register, unregister, isFocused, inspectorId],
  );

  const entry = focusId ? registry.current.get(focusId) : undefined;
  const showInspector = !!focusId && !!entry;

  return (
    <DrillDeckContext.Provider value={value}>
      <div className={`ddrill-deck${showInspector ? ' has-inspector' : ''}`}>
        <div className="ddrill-grid">{children}</div>
        {showInspector && entry ? (
          <aside
            id={inspectorId}
            className="ddrill-inspector"
            aria-label={i18n.t('performance:drillUi.inspectorAria', { title: entry.title })}
          >
            <div className="ddrill-inspector-head">
              <h3 className="ddrill-inspector-title">{entry.title}</h3>
              <button
                ref={closeBtnRef}
                type="button"
                className="ddrill-inspector-close"
                onClick={() => setFocusId(null)}
                aria-label={i18n.t('performance:drillUi.closeDetails')}
              >
                <Icon name="close" size={16} />
              </button>
            </div>
            <div className="ddrill-inspector-body">{entry.body}</div>
          </aside>
        ) : null}
      </div>
    </DrillDeckContext.Provider>
  );
}
